import { action, computed, makeObservable, observable } from "mobx";
import { GoogleUser, User, UserOrderBy, UserRoles, UserStatus } from "./types";
import AuthService from "../../services/AuthService";
import { LoginResponse } from "../../services/AuthService/AuthService";

const defaultUser: User = {
  id: "",
  roles: [],
  email: "",
  emailValid: false,
  firstName: "",
  lastName: "",
  phone: "",
  phoneValid: false,
  status: UserStatus.ACTIVE,
};

class AuthStore {
  @observable user: User = defaultUser;
  @observable token: string = "";
  authService: AuthService = new AuthService();

  constructor() {
    makeObservable(this);
    this.token = localStorage.getItem("token") || "";
  }

  init(): Promise<boolean> {
    return this.fetchUserInformation();
  }

  @computed get isAuthenticated(): boolean {
    return !!this.token;
  }

  @action login(
    email: string,
    password: string,
    captchaId: string,
    captchCode: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService
        .userLogin(email, password, captchaId, captchCode)
        .then((response: LoginResponse) => {
          this.afterLogin(response);
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  @action googleLogin(access_token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.authService
        .googleLogin(access_token)
        .then((response: LoginResponse) => {
          this.afterLogin(response);
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  @action googleRegister(access_token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.authService
        .googleRegister(access_token)
        .then((response: LoginResponse) => {
          this.afterLogin(response);
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  @action appleLogin(access_token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.authService
        .appleLogin(access_token)
        .then((response: LoginResponse) => {
          this.afterLogin(response);
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  @action appleRegister(
    access_token: string,
    given_name: string,
    family_name: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.authService
        .appleRegister(access_token, given_name, family_name)
        .then((response: LoginResponse) => {
          this.afterLogin(response);
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  @action logout(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authService
        .userLogout()
        .then(() => {
          this.afterLogout();
          resolve();
        })
        .catch((error) => reject(error));
    });
  }

  @action forgotPassword(
    email: string,
    captchaId: string,
    captchaCode: string
  ): Promise<void> {
    return this.authService.forgotPassword(email, captchaId, captchaCode);
  }

  getGoogleUser(accessToken: string): Promise<GoogleUser> {
    return this.authService.fetchGoogleUser(accessToken);
  }

  getUser(): Promise<User> {
    return this.authService.fetchUser();
  }

  searchUsers(
    keyword: string,
    orderBy?: UserOrderBy,
    page?: number,
    size?: number
  ): Promise<{ users: User[]; count: number }> {
    return this.authService.searchUsers(
      keyword,
      orderBy || UserOrderBy.FIRST_NAME_ASC,
      page || 0,
      size || 10
    );
  }

  fetchPendingRegistrations(): Promise<User[]> {
    return this.authService.fetchPendingRegistrations();
  }

  fetchUserInformation(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        this.getUser()
          .then((user) => {
            if (user?.id) {
              this.user = user;
              resolve(true);
            } else {
              this.afterLogout();
              resolve(false);
            }
          })
          .catch((error) => reject(error));
      } catch (error) {
        this.afterLogout();
        resolve(false);
      }
    });
  }

  registerUser(
    email: string,
    password: string,
    phone: string,
    captchaId: string,
    captchaCode: string
  ): Promise<any> {
    return this.authService.registerUser(
      email,
      password,
      phone,
      captchaId,
      captchaCode
    );
  }

  userHasRole(roleName: UserRoles): boolean {
    return (this.user?.roles || []).includes(roleName);
  }

  updateUser(user: User): Promise<User> {
    return this.authService.updateUser(user);
  }

  approveLawyerApplication(lawyer: User): Promise<void> {
    return this.authService.approveLawyer(lawyer);
  }

  rejectLawyerApplication(lawyer: User): Promise<void> {
    return this.authService.rejectLawyer(lawyer);
  }

  afterLogin(response: LoginResponse): void {
    this.token = response.token;
    localStorage.setItem("token", this.token);
  }

  afterLogout(): void {
    this.token = "";
    this.user = defaultUser;
    localStorage.removeItem("token");

    if (window.plugins?.googleplus) {
      window.plugins?.googleplus?.logout();
    }
  }

  sendEmailCode(email: string): Promise<void> {
    return this.authService.sendEmailCode({
      newEmail: email,
    });
  }

  validateEmailCode(code: string, email?: string): Promise<boolean> {
    return this.authService.validateEmailCode(code, email);
  }

  /*
  sendPhoneCode(): Promise<void> {
    return this.authService.sendPhoneCode();
  }

  validatePhoneCode(code: string): Promise<boolean> {
    return this.authService.validatePhoneCode(code);
  }
  */

  validateHash(hash: string): Promise<void> {
    return this.authService.validateHash(hash);
  }

  resetPassword(hash: string, password: string): Promise<void> {
    return this.authService.resetPassword(hash, password);
  }

  changePassword(password: string): Promise<void> {
    return this.authService.changePassword(password);
  }

  deactivateUser(): Promise<void> {
    return this.authService.deactivateUser();
  }
}

export default AuthStore;
